import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useOrg } from "../lib/org";
import {
  Button,
  ErrorState,
  Field,
  Modal,
  Money,
  PageHeader,
  Select,
  Spinner,
  TextField,
} from "../components/ui";
import { DataTable } from "../components/DataTable";
import { centsToDollars, dollarsToCents } from "../lib/format";
import type { Account, Item } from "../lib/models";

export default function Items() {
  const { currentOrg } = useOrg();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const currency = currentOrg?.baseCurrency ?? "USD";

  const { data, isLoading, error } = useQuery({
    queryKey: ["items", currentOrg?.id],
    queryFn: () => api.get<Item[]>("/items"),
    enabled: !!currentOrg,
  });

  return (
    <div>
      <PageHeader
        title="Products & Services"
        subtitle="Items you sell or buy"
        action={<Button onClick={() => setOpen(true)}>New item</Button>}
      />
      {isLoading && <Spinner />}
      {error && <ErrorState error={error} />}
      {data && (
        <DataTable
          rows={data}
          rowKey={(r) => r.id}
          columns={[
            {
              key: "name",
              header: "Name",
              render: (r) => (
                <span className="font-medium text-gray-900">{r.name}</span>
              ),
            },
            { key: "sku", header: "SKU", render: (r) => r.sku ?? "—" },
            {
              key: "type",
              header: "Type",
              render: (r) => (
                <span className="capitalize text-gray-600">
                  {r.type.replace("_", "-")}
                </span>
              ),
            },
            {
              key: "price",
              header: "Sales price",
              align: "right",
              render: (r) => <Money cents={r.salesPrice} currency={currency} />,
            },
            {
              key: "cost",
              header: "Cost",
              align: "right",
              render: (r) => <Money cents={r.purchaseCost} currency={currency} />,
            },
          ]}
        />
      )}

      <NewItemModal
        open={open}
        onClose={() => setOpen(false)}
        onCreated={() => {
          setOpen(false);
          qc.invalidateQueries({ queryKey: ["items"] });
        }}
      />
    </div>
  );
}

function NewItemModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { currentOrg } = useOrg();
  const { data: accounts } = useQuery({
    queryKey: ["accounts", currentOrg?.id],
    queryFn: () => api.get<Account[]>("/accounts"),
    enabled: open && !!currentOrg,
  });
  const incomeAccounts = (accounts ?? []).filter((a) => a.type === "income");
  const expenseAccounts = (accounts ?? []).filter((a) => a.type === "expense");

  const [form, setForm] = useState({
    name: "",
    sku: "",
    type: "service" as Item["type"],
    salesPrice: "0.00",
    purchaseCost: "0.00",
    incomeAccountId: "",
    expenseAccountId: "",
  });

  const create = useMutation({
    mutationFn: () =>
      api.post("/items", {
        name: form.name,
        sku: form.sku || undefined,
        type: form.type,
        salesPrice: dollarsToCents(form.salesPrice),
        purchaseCost: dollarsToCents(form.purchaseCost),
        incomeAccountId: form.incomeAccountId || undefined,
        expenseAccountId: form.expenseAccountId || undefined,
        taxable: true,
      }),
    onSuccess: onCreated,
  });

  return (
    <Modal open={open} onClose={onClose} title="New product / service">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <div className="grid grid-cols-2 gap-4">
          <Field label="Name">
            <TextField
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </Field>
          <Field label="SKU">
            <TextField
              value={form.sku}
              onChange={(e) => setForm({ ...form, sku: e.target.value })}
            />
          </Field>
        </div>
        <Field required label="Type">
          <Select
            value={form.type}
            onChange={(e) =>
              setForm({ ...form, type: e.target.value as Item["type"] })
            }
          >
            <option value="service">Service</option>
            <option value="inventory">Inventory</option>
            <option value="non_inventory">Non-inventory</option>
          </Select>
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Sales price">
            <TextField
              type="number"
              step="0.01"
              value={form.salesPrice}
              onChange={(e) => setForm({ ...form, salesPrice: e.target.value })}
            />
          </Field>
          <Field label="Purchase cost">
            <TextField
              type="number"
              step="0.01"
              value={form.purchaseCost}
              onChange={(e) => setForm({ ...form, purchaseCost: e.target.value })}
            />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Income account">
            <Select
              value={form.incomeAccountId}
              onChange={(e) =>
                setForm({ ...form, incomeAccountId: e.target.value })
              }
            >
              <option value="">— select —</option>
              {incomeAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code} {a.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Expense account">
            <Select
              value={form.expenseAccountId}
              onChange={(e) =>
                setForm({ ...form, expenseAccountId: e.target.value })
              }
            >
              <option value="">— select —</option>
              {expenseAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code} {a.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        {create.error && <ErrorState error={create.error} />}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? "Saving…" : "Create item"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// (centsToDollars retained for symmetry with edit flows)
void centsToDollars;
